<template>
  <div class="checkin-pool-container">
    <div class="header-controls">
      <h2 class="title">Aktuelle Check-Ins</h2>
      <v-progress-circular v-if="fetchingData" indeterminate color="primary" size="24"></v-progress-circular>
      <v-chip v-else small color="primary" outlined>{{ sortedPool.length }}</v-chip>
    </div>

    <v-row dense class="mb-4">
      <v-col cols="12" md="8">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Kunden suchen..."
          outlined
          dense
          hide-details
          rounded
          clearable></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-switch class="mt-0 pt-0 ml-md-3" inset hide-details v-model="filterCheckedoutMembers" label="Nur Eingecheckte"></v-switch>
      </v-col>
    </v-row>

    <div v-if="!fetchingData && sortedPool.length === 0" class="empty-state">
      <v-icon size="60" color="grey lighten-2">mdi-account-off-outline</v-icon>
      <p class="mt-4 grey--text text--darken-1">Keine Check-Ins gefunden.</p>
    </div>

    <v-virtual-scroll v-else :items="sortedPool" :item-height="90" class="virtual-scroll-container">
      <template v-slot:default="{ item }">
        <v-card class="checkin-card" @click="selectEntry(item)" outlined>
          <div class="d-flex align-center pa-3">
            <v-badge :color="isCheckedIn(item) ? 'green accent-4' : 'red lighten-2'" dot bordered offset-y="18" offset-x="12">
              <v-avatar size="56">
                <img v-if="getPicture(item)" :src="getPicture(item)" alt="Profilbild" loading="lazy" />
                <span v-else class="white--text text-h6">{{ getInitials(item) }}</span>
              </v-avatar>
            </v-badge>

            <div class="flex-grow-1 ml-4">
              <div class="customer-name">
                {{ item.firstName }} {{ item.lastName }}
                <v-icon v-if="checkIfUserBirthday(item)" small color="amber" class="ml-1">mdi-cake-variant</v-icon>
              </div>
              <div class="checkin-time">
                <v-icon small class="mr-1">mdi-clock-in</v-icon>
                {{ formatDate(item.checkinDate) }} Uhr
                <span v-if="item.checkoutDate" class="ml-2">
                  <v-icon small class="mr-1">mdi-clock-out</v-icon>
                  {{ formatDate(item.checkoutDate) }} Uhr
                </span>
              </div>
            </div>

            <div class="d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" :color="interactionColors[item._id]" class="mr-4"> mdi-message-text-clock-outline </v-icon>
                </template>
                <span>{{ interactionStatusText(item) }}</span>
              </v-tooltip>

              <v-btn icon color="primary" class="action-btn">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card>
      </template>
    </v-virtual-scroll>
  </div>
</template>

<script>
import api from "@/api"
import { debounce } from "lodash"

export default {
  name: "CheckinPool",
  data() {
    return {
      fetchingData: true,
      pool: [],
      search: "",
      filterCheckedoutMembers: true,
      lastUpdatedAt: new Date().setHours(0, 0, 0, 0), // Start of today
      pollingInterval: null,
    }
  },
  computed: {
    interactionColors() {
      return this.pool.reduce((colors, entry) => {
        colors[entry._id] = this.calculateInteractionColor(entry)
        return colors
      }, {})
    },
    sortedPool() {
      // Memoized search term for performance
      const searchLower = this.search ? this.search.toLowerCase().trim() : ""

      return this.pool
        .filter((e) => {
          if (!searchLower) return true
          const fullName = `${e.firstName} ${e.lastName}`.toLowerCase()
          return fullName.includes(searchLower)
        })
        .sort((a, b) => {
          // Sort by check-in date, newest first
          return new Date(b.checkinDate) - new Date(a.checkinDate)
        })
    },
  },
  watch: {
    filterCheckedoutMembers() {
      // Full reload when the filter changes
      this.fetchCheckinPool(true)
    },
  },
  created() {
    this.fetchCheckinPool(true)
    this.startPolling()
  },
  beforeDestroy() {
    clearInterval(this.pollingInterval)
  },
  methods: {
    selectEntry(entry) {
      if (entry.user && entry.user._id) {
        this.$emit("entry-selected", entry)
      } else {
        this.$toast.warning("Dieser Gast hat keinen verknüpften roody Account.")
      }
    },
    async fetchCheckinPool(fullReload = false) {
      this.fetchingData = true
      try {
        const timestamp = fullReload ? new Date().setHours(0, 0, 0, 0) : this.lastUpdatedAt
        const checkinData = await api.fetchCheckinPool(this.filterCheckedoutMembers, timestamp)

        if (fullReload) {
          this.pool = checkinData
        } else {
          checkinData.forEach((newItem) => {
            const index = this.pool.findIndex((item) => item._id === newItem._id)
            if (index !== -1) {
              this.$set(this.pool, index, newItem)
            } else {
              this.pool.unshift(newItem) // Add new check-ins to the top
            }
          })
        }

        // Update lastUpdatedAt with the latest timestamp from the fetched data
        if (checkinData.length > 0) {
          this.lastUpdatedAt = checkinData.reduce((latest, entry) => {
            const entryTimestamp = new Date(entry.updatedAt).getTime()
            return entryTimestamp > latest ? entryTimestamp : latest
          }, this.lastUpdatedAt)
        }
      } catch (error) {
        console.error("Fehler beim Laden der Checkins:", error)
        this.$toast.error("Check-Ins konnten nicht geladen werden.")
      } finally {
        this.fetchingData = false
      }
    },
    startPolling() {
      this.pollingInterval = setInterval(() => {
        this.fetchCheckinPool(false) // Only fetch updates
      }, 60000) // Poll every 60 seconds
    },
    getPicture(entry) {
      if (entry.user?.bild) {
        return `/img/profilbilder/${entry.user.bild}`
      }
      if (entry.user?.tac_image) {
        return `data:image/jpeg;base64,${entry.user.tac_image}`
      }
      return null
    },
    getInitials(entry) {
      const first = entry.firstName ? entry.firstName[0] : ""
      const last = entry.lastName ? entry.lastName[0] : ""
      return `${first}${last}`.toUpperCase()
    },
    isCheckedIn(entry) {
      // A user is checked in if there is no checkoutDate or if checkoutDate is before checkinDate
      return !entry.checkoutDate || new Date(entry.checkoutDate) < new Date(entry.checkinDate)
    },
    formatDate(dateString) {
      if (!dateString) return ""
      return new Date(dateString).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    checkIfUserBirthday(entry) {
      if (!entry.birthday) return false
      const today = new Date()
      const birthday = new Date(entry.birthday)
      return today.getDate() === birthday.getDate() && today.getMonth() === birthday.getMonth()
    },
    calculateInteractionColor(entry) {
      if (!entry.customerInteractions || entry.customerInteractions.length === 0) return "red"
      const latestInteractionDate = entry.customerInteractions[0]?.interactions?.date
      if (!latestInteractionDate) return "red"

      const monthsDiff = (new Date() - new Date(latestInteractionDate)) / (1000 * 60 * 60 * 24 * 30.44) // Avg months
      if (monthsDiff > 3) return "orange"
      return "green"
    },
    interactionStatusText(entry) {
      if (!entry.customerInteractions || entry.customerInteractions.length === 0) return "Keine Interaktion erfasst"
      const latestDate = entry.customerInteractions[0]?.interactions?.date
      if (!latestDate) return "Keine Interaktion erfasst"
      return `Letzte Interaktion: ${new Date(latestDate).toLocaleDateString("de-CH")}`
    },
  },
}
</script>

<style scoped>
.checkin-pool-container {
  padding: 16px;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.virtual-scroll-container {
  height: 65vh; /* Adjust height as needed */
  overflow-y: auto;
  padding-right: 4px;
}

.checkin-card {
  margin-bottom: 12px;
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  border: 1px solid #e0e0e0 !important;
}

.checkin-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
  border-color: var(--v-primary-base) !important;
}

.customer-name {
  font-weight: 600;
  font-size: 1rem;
  color: #333;
}

.checkin-time {
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.action-btn {
  background-color: rgba(var(--v-primary-base-rgb), 0.1);
  transition: background-color 0.2s;
}

.checkin-card:hover .action-btn {
  background-color: var(--v-primary-base);
  color: white !important;
}

.empty-state {
  text-align: center;
  padding: 48px 0;
}
</style>
